import { useViewStore } from '@/store/view.store'

// Logical "day" for habit/recurring completions and streaks — mirrors the
// backend's convex/lib/day.ts. A user's day-start hour (default 3am, hydrated
// into the view store from server preferences) shifts the boundary so late-night
// completions count toward the day that just ended.

export function currentDayStartHour(): number {
  return useViewStore.getState().dayStartHour
}

/** Local YYYY-MM-DD for an epoch-ms timestamp, honoring the day-start hour. */
export function logicalDayStr(ms: number, dayStartHour: number = currentDayStartHour()): string {
  const tz = new Date().getTimezoneOffset()
  return new Date(ms - tz * 60_000 - dayStartHour * 3_600_000).toISOString().slice(0, 10)
}

/** Today's logical-day key. */
export function todayKey(): string {
  return logicalDayStr(Date.now())
}

/** Local-midnight Date for a day key, for weekday/calendar maths on logical days. */
export const keyToDate = (key: string) => new Date(`${key}T00:00:00`)

// "Take a breather" (away/pause) window helpers — mirror convex/lib/day.ts.
// Inclusive logical-day keys (YYYY-MM-DD); `until` omitted = indefinite.

export function isOnBreather(
  from: string | undefined | null,
  until: string | undefined | null,
  key: string = todayKey(),
): boolean {
  if (!from) return false
  if (key < from) return false
  if (!until) return true
  return key <= until
}

/** Logical-day keys covered by a breather window, clamped to [from, upTo]. */
export function breatherDayKeys(
  from: string | undefined | null,
  until: string | undefined | null,
  upToKey: string = todayKey(),
  maxDays = 120,
): string[] {
  if (!from) return []
  const end = until && until < upToKey ? until : upToKey
  if (end < from) return []
  const fromMs = Date.parse(`${from}T00:00:00Z`)
  const keys: string[] = []
  let ms = Date.parse(`${end}T00:00:00Z`)
  for (let i = 0; i < maxDays && ms >= fromMs; i++) {
    keys.push(new Date(ms).toISOString().slice(0, 10))
    ms -= 86_400_000
  }
  return keys
}

export const BACKDATE_DAYS = 7

// Mirrors the backend window: days more than a week before today can't be ticked.
export const isTooFarBack = (key: string, today: string = todayKey()) =>
  Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${key}T00:00:00Z`)) / 86_400_000) >
  BACKDATE_DAYS
